/**
 * The hand-off between the Service Worker and the page for a file that arrived
 * from Android's share sheet.
 *
 * WhatsApp's "Export chat" posts the zip to us as a navigation — the worker is
 * what receives it, and the worker cannot hand a File to a page that does not
 * exist yet. So it goes in IndexedDB, which survives both the redirect and the
 * worker being killed, and the page takes it on arrival.
 *
 * Both sides open the database through here on purpose: whoever gets there
 * first creates the store, and a page that created it without one would make
 * every later transaction throw.
 */

const DB = 'okazip-share';
const STORE = 'inbox';
const KEY = 'archive';

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function done(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = tx.onerror = () => reject(tx.error);
  });
}

/** Service Worker side: park the shared file before redirecting to the app. */
export async function putSharedFile(file: File): Promise<void> {
  const db = await open();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(file, KEY);
    await done(tx);
  } finally { db.close(); }
}

/** Page side: take it, and leave nothing behind — it is a one-shot delivery. */
export async function takeSharedFile(): Promise<File | null> {
  const db = await open();
  try {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    const got = await new Promise<File | null>((resolve, reject) => {
      const r = store.get(KEY);
      r.onsuccess = () => resolve((r.result as File | undefined) ?? null);
      r.onerror = () => reject(r.error);
    });
    store.delete(KEY);
    await done(tx);
    return got;
  } finally { db.close(); }
}
